import { CompleteKundali, PlanetName, ZodiacSign } from '../types/kundali';
import { NAKSHATRAS, getSavByBhava, getBavByBhava } from './vedicEngine';

export interface GemstoneInfo {
  planet: PlanetName;
  nameEn: string;
  nameHi: string;
  sanskritName: string;
  primaryStone: string;
  substitutes: string[];
  metal: string;
  finger: string;
  fingerHi: string;
  wearingDay: string;
  wearingDayHi: string;
  horaTime: string;
  beejaMantra: string;
  vedicMantra: string;
  color: string;
  hexColor: string;
  rattiFormula: string;
}

export const MASTER_GEMSTONES: Record<PlanetName, GemstoneInfo> = {
  Sun: {
    planet: 'Sun',
    nameEn: 'Sun',
    nameHi: 'Surya',
    sanskritName: 'Manikyam (Manikya)',
    primaryStone: 'Ruby (Manikya)',
    substitutes: ['Red Garnet', 'Star Ruby', 'Red Spinel'],
    metal: 'Gold or Copper',
    finger: 'Ring Finger (Anamika)',
    fingerHi: 'Anamika (Ring Finger)',
    wearingDay: 'Sunday Morning (Sunrise / Shukla Paksha)',
    wearingDayHi: 'Ravivar Morning (Sunrise, Shukla Paksha)',
    horaTime: 'Sun Hora (Sunrise to 1 hour after)',
    beejaMantra: 'Om Hraam Hreem Hroum Sah Suryaya Namah',
    vedicMantra: 'Om Akrishnena Rajasa Vartamano Niveshayannamritam Martyam Cha. Hiranyayena Savita Rathena Devo Yati Bhuvanani Pashyan.',
    color: 'Crimson / Pigeon Blood Red',
    hexColor: '#E11D48',
    rattiFormula: '1 Ratti per 10-12 kg body weight (Min 4.25 Ratti)'
  },
  Moon: {
    planet: 'Moon',
    nameEn: 'Moon',
    nameHi: 'Chandra',
    sanskritName: 'Mukta / Mauktikam (Moti)',
    primaryStone: 'Natural Pearl (Moti)',
    substitutes: ['Moonstone (Chandrakanta Mani)', 'White Coral', 'South Sea Cultured Pearl'],
    metal: 'Pure Silver',
    finger: 'Little Finger (Kanishtha) or Ring Finger',
    fingerHi: 'Kanishthika (Little Finger) or Anamika',
    wearingDay: 'Monday Evening (Sunset) or Morning',
    wearingDayHi: 'Somavar Evening or Morning',
    horaTime: 'Moon Hora',
    beejaMantra: 'Om Shraam Shreem Shroum Sah Chandraya Namah',
    vedicMantra: 'Om Imam Deva Asapatnam Suvadhwam Mahate Kshatraya Mahate Jyeshthaya Mahate Janarajyayendrasyendriyaya.',
    color: 'Milky White / Iridescent',
    hexColor: '#F8FAFC',
    rattiFormula: 'Min 5.25 - 7.25 Ratti (Natural Basra or South Sea)'
  },
  Mars: {
    planet: 'Mars',
    nameEn: 'Mars',
    nameHi: 'Mangal',
    sanskritName: 'Pravalam / Vidruma (Moonga)',
    primaryStone: 'Red Coral (Moonga)',
    substitutes: ['Red Carnelian', 'Red Jasper', 'Red Agate'],
    metal: 'Copper, Gold, or Panchdhatu',
    finger: 'Ring Finger (Anamika)',
    fingerHi: 'Anamika (Ring Finger)',
    wearingDay: 'Tuesday Morning (Sunrise)',
    wearingDayHi: 'Mangalvar Morning (Sunrise)',
    horaTime: 'Mars Hora',
    beejaMantra: 'Om Kraam Kreem Kroum Sah Bhaumaya Namah',
    vedicMantra: 'Om Agnimurdha Divah Kakutpatih Prithivya Ayam. Apam Retamsi Jinvati.',
    color: 'Ox-blood Red / Deep Vermilion',
    hexColor: '#DC2626',
    rattiFormula: '1 Ratti per 10 kg body weight (Min 6.25 Ratti)'
  },
  Mercury: {
    planet: 'Mercury',
    nameEn: 'Mercury',
    nameHi: 'Budha',
    sanskritName: 'Marakatam (Panna)',
    primaryStone: 'Emerald (Panna)',
    substitutes: ['Peridot', 'Green Tourmaline (Verdelite)', 'Green Onyx', 'Tsavorite'],
    metal: 'Gold, Silver, or Bronze',
    finger: 'Little Finger (Kanishtha)',
    fingerHi: 'Kanishthika (Little Finger)',
    wearingDay: 'Wednesday Morning (2 hours after sunrise)',
    wearingDayHi: 'Budhavar Morning',
    horaTime: 'Mercury Hora',
    beejaMantra: 'Om Braam Breem Broum Sah Budhaya Namah',
    vedicMantra: 'Om Udbudhyasvagne Prati Jagrihi Tvamistapurte Sam Srijethamayam Cha.',
    color: 'Deep Lush Grass Green',
    hexColor: '#059669',
    rattiFormula: 'Min 3.25 - 6.25 Ratti (Zambian or Colombian)'
  },
  Jupiter: {
    planet: 'Jupiter',
    nameEn: 'Jupiter',
    nameHi: 'Brihaspati / Guru',
    sanskritName: 'Pushparajam (Pukhraj)',
    primaryStone: 'Yellow Sapphire (Pukhraj)',
    substitutes: ['Yellow Topaz', 'Heliodor (Golden Beryl)', 'Citrine (Sunela)'],
    metal: 'Yellow Gold or Brass',
    finger: 'Index Finger (Tarjani)',
    fingerHi: 'Tarjani (Index Finger)',
    wearingDay: 'Thursday Morning (Brahma Muhurta or Sunrise)',
    wearingDayHi: 'Guruvar Morning (Brahma Muhurta or Sunrise)',
    horaTime: 'Jupiter Hora',
    beejaMantra: 'Om Graam Greem Groum Sah Gurave Namah',
    vedicMantra: 'Om Brihaspate Ati Yadaryo Arhad Dyumadvibhati Kratumajjaneshu. Yaddidayacchavasa Ritaprajata Tadasmasu Dravinam Dhehi Chitram.',
    color: 'Canary Yellow / Lemon Yellow',
    hexColor: '#EAB308',
    rattiFormula: 'Min 4.25 - 7.25 Ratti (Ceylon/Sri Lankan)'
  },
  Venus: {
    planet: 'Venus',
    nameEn: 'Venus',
    nameHi: 'Shukra',
    sanskritName: 'Vajram (Heera)',
    primaryStone: 'Diamond (Heera) or White Sapphire (Safed Pukhraj)',
    substitutes: ['White Zircon', 'Australian Opal', 'White Topaz', 'Goshenite'],
    metal: 'Platinum, White Gold, or Pure Silver',
    finger: 'Middle Finger (Madhyama), Little Finger, or Thumb',
    fingerHi: 'Madhyama (Middle Finger), Kanishthika or Thumb',
    wearingDay: 'Friday Morning (Sunrise / Shukla Paksha)',
    wearingDayHi: 'Shukravar Morning (Sunrise, Shukla Paksha)',
    horaTime: 'Venus Hora',
    beejaMantra: 'Om Draam Dreem Droum Sah Shukraya Namah',
    vedicMantra: 'Om Annatparisruto Rasam Brahmana Vyapibat Kshatram Payah Somam Prajapatih.',
    color: 'Colorless Brilliant Sparkle / Crystal Clear',
    hexColor: '#38BDF8',
    rattiFormula: 'Diamond: 0.50 - 1.50 Carats; White Sapphire/Opal: 5 - 8 Ratti'
  },
  Saturn: {
    planet: 'Saturn',
    nameEn: 'Saturn',
    nameHi: 'Shani',
    sanskritName: 'Indraneelam (Neelam)',
    primaryStone: 'Blue Sapphire (Neelam)',
    substitutes: ['Amethyst (Jamunia)', 'Iolite (Neeli)', 'Blue Topaz', 'Lapis Lazuli (Lajward)'],
    metal: 'Ashtadhatu, Iron, Panchdhatu, or White Gold',
    finger: 'Middle Finger (Madhyama)',
    fingerHi: 'Madhyama (Middle Finger)',
    wearingDay: 'Saturday Twilight / Night (after trial test)',
    wearingDayHi: 'Shanivar Twilight / Night (after 3-day trial test)',
    horaTime: 'Saturn Hora',
    beejaMantra: 'Om Praam Preem Proum Sah Shanishcharaya Namah',
    vedicMantra: 'Om Shanno Devirabhishtaye Apo Bhavantu Peetaye. Sham Yorbhi Sravantu Nah.',
    color: 'Cornflower Blue / Royal Velvet Blue',
    hexColor: '#2563EB',
    rattiFormula: 'Min 4.25 - 7.25 Ratti (Kashmir or Ceylon)'
  },
  Rahu: {
    planet: 'Rahu',
    nameEn: 'Rahu',
    nameHi: 'Rahu',
    sanskritName: 'Gomedakam (Gomed)',
    primaryStone: 'Hessonite Garnet (Gomed)',
    substitutes: ['Spessartite Garnet', 'Honey Amber', 'Brown Tourmaline'],
    metal: 'Ashtadhatu, Silver, or Panchdhatu',
    finger: 'Middle Finger (Madhyama) or Little Finger',
    fingerHi: 'Madhyama or Kanishthika',
    wearingDay: 'Saturday Night or Wednesday Night (Krishna/Shukla Paksha)',
    wearingDayHi: 'Shanivar or Budhavar Night (Krishna/Shukla Paksha)',
    horaTime: 'Rahu Kala / Saturn Hora at night',
    beejaMantra: 'Om Bhraam Bhreem Bhroum Sah Rahave Namah',
    vedicMantra: 'Om Kaya Nashchitra A Bhuwadutee Sadavridhah Sakha. Kaya Shachishthaya Vrita.',
    color: 'Honey Brown / Cow-urine (Gomutra) Color',
    hexColor: '#B45309',
    rattiFormula: 'Min 5.25 - 8.25 Ratti (Ceylon Honey-colored)'
  },
  Ketu: {
    planet: 'Ketu',
    nameEn: 'Ketu',
    nameHi: 'Ketu',
    sanskritName: 'Vaiduryam (Lehsuniya)',
    primaryStone: "Cat's Eye Chrysoberyl (Lehsuniya)",
    substitutes: ["Tiger's Eye", "Hawk's Eye", 'Cat’s Eye Tourmaline'],
    metal: 'Silver or Panchdhatu',
    finger: 'Ring Finger (Anamika) or Middle Finger',
    fingerHi: 'Anamika or Madhyama Finger',
    wearingDay: 'Tuesday Night or Thursday Night',
    wearingDayHi: 'Mangalvar or Guruvar Night',
    horaTime: 'Mars or Jupiter Hora',
    beejaMantra: 'Om Sraam Sreem Sroum Sah Ketave Namah',
    vedicMantra: 'Om Ketum Krinvannaketave Pesho Marya Apeshase. Samushadbhirajayathah.',
    color: 'Chrysoberyl Greenish-Yellow with Silvery Ray',
    hexColor: '#84CC16',
    rattiFormula: 'Min 3.5 - 6.25 Ratti (Chrysoberyl with crisp chatoyant band)'
  },
  Ascendant: {
    planet: 'Sun',
    nameEn: 'Sun',
    nameHi: 'Surya',
    sanskritName: 'Manikyam (Manikya)',
    primaryStone: 'Ruby (Manikya)',
    substitutes: ['Red Garnet'],
    metal: 'Gold',
    finger: 'Ring Finger',
    fingerHi: 'Anamika',
    wearingDay: 'Sunday',
    wearingDayHi: 'Ravivar',
    horaTime: 'Sun Hora',
    beejaMantra: 'Om Hraam Hreem Hroum Sah Suryaya Namah',
    vedicMantra: '',
    color: 'Red',
    hexColor: '#E11D48',
    rattiFormula: 'Min 4 Ratti'
  }
};

export const ZODIAC_LORDS_ARRAY: PlanetName[] = [
  'Mars',    // Aries (0)
  'Venus',   // Taurus (1)
  'Mercury', // Gemini (2)
  'Moon',    // Cancer (3)
  'Sun',     // Leo (4)
  'Mercury', // Virgo (5)
  'Venus',   // Libra (6)
  'Mars',    // Scorpio (7)
  'Jupiter', // Sagittarius (8)
  'Saturn',  // Capricorn (9)
  'Saturn',  // Aquarius (10)
  'Jupiter'  // Pisces (11)
];

export const SIGN_NAMES: ZodiacSign[] = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

export const SIGN_NAMES_HI: Record<ZodiacSign, string> = {
  Aries: 'Mesha', Taurus: 'Vrishabha', Gemini: 'Mithuna', Cancer: 'Karka',
  Leo: 'Simha', Virgo: 'Kanya', Libra: 'Tula', Scorpio: 'Vrishchika',
  Sagittarius: 'Dhanu', Capricorn: 'Makara', Aquarius: 'Kumbha', Pisces: 'Meena'
};

// Natural Planetary Enmities & Friendships (Naisargika Sambandha)
export const PLANET_RELATIONSHIPS: Record<PlanetName, { friends: PlanetName[]; enemies: PlanetName[]; neutral: PlanetName[] }> = {
  Sun: {
    friends: ['Moon', 'Mars', 'Jupiter'],
    enemies: ['Venus', 'Saturn', 'Rahu', 'Ketu'],
    neutral: ['Mercury']
  },
  Moon: {
    friends: ['Sun', 'Mercury'],
    enemies: ['Rahu', 'Ketu'],
    neutral: ['Mars', 'Jupiter', 'Venus', 'Saturn']
  },
  Mars: {
    friends: ['Sun', 'Moon', 'Jupiter'],
    enemies: ['Mercury', 'Rahu'],
    neutral: ['Venus', 'Saturn', 'Ketu']
  },
  Mercury: {
    friends: ['Sun', 'Venus'],
    enemies: ['Moon'],
    neutral: ['Mars', 'Jupiter', 'Saturn', 'Rahu', 'Ketu']
  },
  Jupiter: {
    friends: ['Sun', 'Moon', 'Mars'],
    enemies: ['Mercury', 'Venus'],
    neutral: ['Saturn', 'Rahu', 'Ketu']
  },
  Venus: {
    friends: ['Mercury', 'Saturn', 'Rahu', 'Ketu'],
    enemies: ['Sun', 'Moon'],
    neutral: ['Mars', 'Jupiter']
  },
  Saturn: {
    friends: ['Mercury', 'Venus', 'Rahu'],
    enemies: ['Sun', 'Moon', 'Mars'],
    neutral: ['Jupiter', 'Ketu']
  },
  Rahu: {
    friends: ['Venus', 'Saturn', 'Mercury'],
    enemies: ['Sun', 'Moon', 'Mars'],
    neutral: ['Jupiter', 'Ketu']
  },
  Ketu: {
    friends: ['Mars', 'Jupiter'],
    enemies: ['Sun', 'Moon'],
    neutral: ['Mercury', 'Venus', 'Saturn', 'Rahu']
  },
  Ascendant: {
    friends: [],
    enemies: [],
    neutral: []
  }
};

// Navatara 9 Categories
export interface TaraInfo {
  index: number;
  nameEn: string;
  nameHi: string;
  nature: 'Auspicious' | 'Dangerous' | 'Neutral' | 'Benefic';
  isSafe: boolean;
  meaning: string;
}

export const NAVATARA_TABLE: TaraInfo[] = [
  { index: 0, nameEn: 'Janma', nameHi: 'Janma Tara', nature: 'Neutral', isSafe: true, meaning: 'Physical vitality & self-awareness. Wear only if planet is strong functional benefic.' },
  { index: 1, nameEn: 'Sampat', nameHi: 'Sampat Tara', nature: 'Auspicious', isSafe: true, meaning: 'Bringer of wealth, prosperity, business expansion, and abundance.' },
  { index: 2, nameEn: 'Vipat', nameHi: 'Vipat Tara', nature: 'Dangerous', isSafe: false, meaning: 'Calamity, losses, litigation, and acute disruption. Strictly contraindicated.' },
  { index: 3, nameEn: 'Kshema', nameHi: 'Kshema Tara', nature: 'Auspicious', isSafe: true, meaning: 'Protection, healing, peace of mind, family welfare, and longevity.' },
  { index: 4, nameEn: 'Pratyak', nameHi: 'Pratyak Tara', nature: 'Dangerous', isSafe: false, meaning: 'Obstacles, resistance, backbiting, and friction in achievements. Highly inadvisable.' },
  { index: 5, nameEn: 'Sadhana', nameHi: 'Sadhana Tara', nature: 'Auspicious', isSafe: true, meaning: 'Success in undertakings, professional mastery, spiritual achievements, and elevation.' },
  { index: 6, nameEn: 'Vadha / Naidhana', nameHi: 'Vadha / Naidhana Tara', nature: 'Dangerous', isSafe: false, meaning: 'Associated with severe obstacles or crisis in electional Navatara (Muhurta Chintamani 1.34). [PROJECT_INTERPRETATION] Gemstone wearing is withheld under project policy. [UNCERTAINTY_BOUNDARY] Extending electional Tara to natal gemstone wearing is an interpretive convention.' },
  { index: 7, nameEn: 'Mitra', nameHi: 'Mitra Tara', nature: 'Auspicious', isSafe: true, meaning: 'Friendship, smooth social relationships, allies, and enjoyable gains.' },
  { index: 8, nameEn: 'Param Mitra', nameHi: 'Parama Mitra Tara', nature: 'Auspicious', isSafe: true, meaning: 'Supreme cosmic friend, ultimate blessing, divine protection, and triumph.' }
];

export interface EvaluatedGemstone {
  planet: PlanetName;
  gemstone: GemstoneInfo;
  perspectiveType: 'TRIKONA_LIFELONG' | 'KENDRA_YOGAKARAKA' | 'TEMPORAL_DASHA' | 'JAIMINI_KARAKA' | 'CONTRAINDICATED';
  roleTitleEn: string;
  roleTitleHi: string;
  categoryBadge: string;
  suitabilityScore: number; // 0 - 100
  isRecommended: boolean;
  isContraindicated: boolean;
  reasons: string[];
  cautions: string[];
  temporalWindow?: {
    startDate: string;
    endDate: string;
    dashaType: 'Mahadasha' | 'Antardasha';
    summary: string;
  };
  audits: {
    ruledHouses: number[];
    isLagnaLord: boolean;
    isYogakaraka: boolean;
    isDusthanaLord: boolean;
    isBadhakaLord: boolean;
    isMarakaLord: boolean;
    tara: TaraInfo;
    savBindus: number;
    bavBindus: number;
    savPassed: boolean;
    bavPassed: boolean;
    charaKaraka?: string;
    dignity: string;
    housePlaced: number;
    d9Dignity?: string;
    shadbalaRatio?: number;
  };
}

export interface VedicGemologyAnalysis {
  ascendantSign: ZodiacSign;
  ascendantLord: PlanetName;
  moonSign: ZodiacSign;
  moonLord: PlanetName;
  janmaNakshatra: string;
  
  // Perspective 1: The Core Trikona Lifelong Trinity (1, 5, 9)
  trikonaTrinity: {
    lifeStone: EvaluatedGemstone;     // 1st House Lord (Deha / Jeevan Ratna)
    punyaStone: EvaluatedGemstone;    // 5th House Lord (Dhee / Punya Ratna)
    bhagyaStone: EvaluatedGemstone;   // 9th House Lord (Bhagya Ratna)
  };

  // Perspective 2: Kendradhipati & Yogakaraka School (4, 10 & Yogakaraka)
  kendraYogakaraka: {
    sukhaStone: EvaluatedGemstone;    // 4th House Lord (Peace, Assets, Mother)
    karmaStone: EvaluatedGemstone;    // 10th House Lord (Career, Status, Governance)
    yogakarakaStone?: EvaluatedGemstone; // Dual Kendra+Trikona Lord if applicable
  };

  // Perspective 3: Temporal Dasha Advisor (Active MD / AD stones)
  dashaAdvisor: {
    activeMahadasha: {
      planet: PlanetName;
      startDate: string;
      endDate: string;
      evaluatedGem: EvaluatedGemstone;
      verdict: 'HIGHLY_RECOMMENDED' | 'CONDITIONALLY_SAFE' | 'STRICTLY_AVOID';
      verdictExplanation: string;
    };
    activeAntardasha?: {
      planet: PlanetName;
      startDate: string;
      endDate: string;
      evaluatedGem: EvaluatedGemstone;
      verdict: 'HIGHLY_RECOMMENDED' | 'CONDITIONALLY_SAFE' | 'STRICTLY_AVOID';
      verdictExplanation: string;
    };
  };

  // Perspective 4: Jaimini Chara Karakas
  jaiminiKarakas: {
    atmakaraka: {
      planet: PlanetName;
      degree: string;
      evaluatedGem: EvaluatedGemstone;
      soulPurpose: string;
    };
    amatyakaraka: {
      planet: PlanetName;
      degree: string;
      evaluatedGem: EvaluatedGemstone;
      careerInfluence: string;
    };
  };

  // Perspective 5: Strictly Contraindicated & Toxic Gems (Nishedha Ratna)
  contraindicatedGems: EvaluatedGemstone[];

  // All 9 evaluated planets for reference
  allEvaluations: Record<PlanetName, EvaluatedGemstone>;
}

/**
 * Calculates Badhaka House based on Ascendant nature
 * Movable (Chara - Aries, Cancer, Libra, Capricorn) -> 11th House
 * Fixed (Sthira - Taurus, Leo, Scorpio, Aquarius) -> 9th House
 * Dual (Dwisvabhava - Gemini, Virgo, Sagittarius, Pisces) -> 7th House
 */
export function getBadhakaHouse(ascSignIndex: number): number {
  const nature = ascSignIndex % 3; // 0 = Chara, 1 = Sthira, 2 = Dwisvabhava
  if (nature === 0) return 11;
  if (nature === 1) return 9;
  return 7;
}

/**
 * Computes Navatara Tara Bala for a given planet relative to natal Moon
 */
export function calculatePlanetTara(planetNakIndex: number, moonNakIndex: number): TaraInfo {
  const diff = (planetNakIndex - moonNakIndex + 27) % 27;
  const taraIdx = diff % 9;
  return NAVATARA_TABLE[taraIdx];
}

/**
 * Full Evaluation Engine for a single planet in the native's chart
 */
export function evaluateSinglePlanetGemstone(
  planet: PlanetName,
  kundali: CompleteKundali,
  roleContext?: {
    perspectiveType?: EvaluatedGemstone['perspectiveType'];
    customTitleEn?: string;
    customTitleHi?: string;
    customCategoryBadge?: string;
  }
): EvaluatedGemstone {
  const masterGem = MASTER_GEMSTONES[planet] || MASTER_GEMSTONES.Sun;
  const ascSign = kundali.houses[0]?.sign || 'Aries';
  const ascSignIdx = SIGN_NAMES.indexOf(ascSign);
  const badhakaHouse = getBadhakaHouse(ascSignIdx >= 0 ? ascSignIdx : 0);

  // Find ruled houses
  const ruledHouses: number[] = [];
  kundali.houses.forEach((h) => {
    if (h.signLord === planet) {
      ruledHouses.push(h.houseNumber);
    }
  });

  const isLagnaLord = ruledHouses.includes(1);
  const isKendra = ruledHouses.some(h => [1, 4, 7, 10].includes(h));
  const isTrikona = ruledHouses.some(h => [1, 5, 9].includes(h));
  const isYogakaraka = (ruledHouses.some(h => [4, 7, 10].includes(h)) && ruledHouses.some(h => [5, 9].includes(h)));
  const isDusthanaLord = ruledHouses.some(h => [6, 8, 12].includes(h)) && !isLagnaLord && !isYogakaraka;
  const isBadhakaLord = ruledHouses.includes(badhakaHouse);
  const isMarakaLord = ruledHouses.includes(2) || ruledHouses.includes(7);

  // Planet placement in D1
  const planetPos = kundali.planets?.find(p => p.name === planet);
  const housePlaced = planetPos?.house || 1;
  const dignity = planetPos?.dignity || 'Neutral';
  const isCombust = planetPos?.isCombust || false;

  // Tara Bala
  const moonPlanet = kundali.planets?.find(p => p.name === 'Moon');
  const moonNakIdx = moonPlanet ? moonPlanet.nakshatraIndex : 0;
  const planetNakIdx = planetPos ? planetPos.nakshatraIndex : 0;
  const tara = calculatePlanetTara(planetNakIdx, moonNakIdx);

  // Ashtakavarga
  const savBindus = getSavByBhava(kundali.ashtakvarga, housePlaced, kundali.houses);
  const bavBindus = getBavByBhava(kundali.ashtakvarga, planet, housePlaced, kundali.houses);
  const savPassed = savBindus >= 28;
  const bavPassed = bavBindus >= 4;

  // Chara Karaka check
  const charaItem = kundali.charaKarakas?.find(c => c.planet === planet);
  const charaKaraka = charaItem?.code;

  // Calculate Base Suitability Score
  let score = 50;
  const reasons: string[] = [];
  const cautions: string[] = [];

  if (isLagnaLord) {
    score += 40;
    reasons.push(`Lord of the 1st House (Lagna Lord) — Supreme ruler of self-identity, immune vitality, longevity, and overall fortune.`);
  }

  if (isYogakaraka) {
    score += 45;
    reasons.push(`Rare Yogakaraka planet for ${ascSign} Lagna (rules both Kendra & Trikona) — grants auspicious breakthroughs, authority, and prosperity.`);
  } else {
    if (ruledHouses.includes(9)) {
      score += 30;
      reasons.push(`Rules the 9th House of Bhagya (Divine Luck, Dharma, Higher Wisdom, and Father's Grace).`);
    }
    if (ruledHouses.includes(5)) {
      score += 25;
      reasons.push(`Rules the 5th House of Purva Punya (Intellect, Creative Genius, Mantra Siddhi, and Progeny).`);
    }
    if (ruledHouses.includes(10)) {
      score += 20;
      reasons.push(`Rules the 10th House of Karma (Career Authority, Leadership, Professional Stature, and Public Recognition).`);
    }
    if (ruledHouses.includes(4)) {
      score += 15;
      reasons.push(`Rules the 4th House of Sukha (Inner Emotional Tranquility, Maternal Happiness, Vehicles, and Fixed Assets).`);
    }
  }

  // Deductions for Malefic Factors
  if (isDusthanaLord) {
    if (ruledHouses.includes(8)) {
      score -= 45;
      cautions.push(`Rules the 8th House of chronic vulnerabilities, sudden shocks, litigation, and crisis (*Randhra Bhava*).`);
    }
    if (ruledHouses.includes(6)) {
      score -= 30;
      cautions.push(`Rules the 6th House of debts, enemies, litigation, and acute health afflictions (*Ripu Bhava*).`);
    }
    if (ruledHouses.includes(12)) {
      score -= 25;
      cautions.push(`Rules the 12th House of expenditure, hospitalization, losses, and detachment (*Vyaya Bhava*).`);
    }
  }

  if (isBadhakaLord && !isLagnaLord && !isYogakaraka) {
    score -= 15;
    cautions.push(`Acts as Badhaka Lord for ${ascSign} Lagna — [PROJECT_INTERPRETATION] strengthening it may invite friction or delays. [UNCERTAINTY_BOUNDARY] Badhaka is a horary concept (Prasna Marga 15.111); its extension to natal gemstone wearing is an interpretive convention.`);
  }

  // Tara Bala Modifiers
  if (!tara.isSafe) {
    score -= 25;
    cautions.push(`Falls in natal ${tara.nameEn} Tara (${tara.nameHi}) from Moon — [PROJECT_INTERPRETATION] withheld from stimulation under project policy. [UNCERTAINTY_BOUNDARY] Navatara is an electional rule (Muhurta Chintamani 1.34); its extension to natal gemstone wearing is an interpretive convention.`);
  } else {
    score += 10;
    reasons.push(`Blessed by ${tara.nameEn} Tara (${tara.nameHi}) from Janma Nakshatra — smooth energetic transmission without friction.`);
  }

  // Ashtakavarga Modifiers
  if (savPassed && bavPassed) {
    score += 10;
    reasons.push(`Strong Ashtakavarga reservoir: Placed in house ${housePlaced} with ${savBindus} SAV points (Threshold: 28+) and ${bavBindus} BAV points.`);
  } else if (!savPassed || !bavPassed) {
    score -= 10;
    cautions.push(`Low Ashtakavarga reservoir in house ${housePlaced}: SAV has ${savBindus} points (ideal 28+) or BAV has ${bavBindus} points (ideal 4+).`);
  }

  // Dignity Modifiers
  if (dignity === 'Exalted' || dignity === 'Own House' || dignity === 'Moolatrikona') {
    score += 15;
    reasons.push(`D1 Dignity is robust (${dignity}) in sign of ${planetPos?.sign}.`);
  } else if (dignity === 'Debilitated') {
    score -= 20;
    cautions.push(`Planet is debilitated in D1 (${planetPos?.sign}). Strengthening with primary gemstone must be done with caution (or use substitute Upratna).`);
  }

  // D9 Navamsha Evaluation (Harmonization with gemstoneAdvisorEngine)
  const d9Planet = kundali.charts?.D9?.planets?.find(p => p.name === planet);
  const d9Dignity = d9Planet?.dignity;
  const d9Sign = d9Planet?.sign;
  if (d9Planet) {
    if (d9Dignity === 'Exalted' || d9Dignity === 'Own House' || d9Dignity === 'Moolatrikona') {
      score += 10;
      reasons.push(`D9 Navamsha confirms favorable dignity (${d9Dignity}) in ${d9Sign}.`);
    } else if (d9Dignity === 'Debilitated') {
      score -= 15;
      cautions.push(`Debilitated in D9 Navamsha (${d9Sign}), indicating underlying structural weakness.`);
    }
  }

  // Shadbala Strength Evaluation (Harmonization with gemstoneAdvisorEngine)
  const planetShadbala = kundali.shadbala?.planets?.find(p => p.planet === planet);
  const shadbalaRatio = planetShadbala ? (planetShadbala.ratio ?? planetShadbala.shadbalaRatio) : undefined;
  if (planetShadbala && shadbalaRatio !== undefined) {
    if (shadbalaRatio >= 1.15) {
      score += 5;
      reasons.push(`Robust Shadbala strength (${planetShadbala.totalRupas.toFixed(1)} Rupas, ${shadbalaRatio.toFixed(2)}x requirement).`);
    } else if (shadbalaRatio < 0.95) {
      score -= 10;
      cautions.push(`Deficit in total Shadbala strength (${planetShadbala.totalRupas.toFixed(1)} Rupas, below 1.0x requirement).`);
    }
  }

  // Combustion check (Anukul-vada: Do NOT add bonus points for combustion)
  if (isCombust && planet !== 'Sun') {
    score -= 5;
    cautions.push(`Planet is Combust (Asta) by the Sun; classical Anukul-vada cautions against primary gemstone stimulation, recommending Upratna or Japa/Daana.`);
  }

  // Bound score 0 - 100
  score = Math.max(5, Math.min(98, score));

  // Determine Recommendation vs Contraindication
  const isPureDusthanaLord = (ruledHouses.includes(6) || ruledHouses.includes(8)) && !isLagnaLord && !isYogakaraka && !ruledHouses.some(h => [1, 5, 9].includes(h));
  const isNaidhanaTara = tara.nameEn === 'Naidhana';
  const isContraindicated = isPureDusthanaLord || isNaidhanaTara || (!tara.isSafe && !isLagnaLord) || (isDusthanaLord && !isLagnaLord && !isYogakaraka && score < 50);

  if (isContraindicated) {
    score = Math.min(score, 40);
  }
  const isRecommended = score >= 65 && !isContraindicated;

  return {
    planet,
    gemstone: masterGem,
    perspectiveType: roleContext?.perspectiveType || (isContraindicated ? 'CONTRAINDICATED' : isYogakaraka || isTrikona ? 'TRIKONA_LIFELONG' : 'KENDRA_YOGAKARAKA'),
    roleTitleEn: roleContext?.customTitleEn || (isLagnaLord ? 'Life Stone (Jeevan Ratna)' : isYogakaraka ? 'Yogakaraka Supreme Stone' : ruledHouses.includes(9) ? 'Fortune Stone (Bhagya Ratna)' : ruledHouses.includes(5) ? 'Intellect Stone (Punya Ratna)' : ruledHouses.includes(10) ? 'Karma / Career Stone' : ruledHouses.includes(4) ? 'Sukha / Inner Peace Stone' : `${planet} Gemstone`),
    roleTitleHi: roleContext?.customTitleHi || (isLagnaLord ? 'Jeevan Ratna (Lagna Lord)' : isYogakaraka ? 'Yogakaraka Ratna' : ruledHouses.includes(9) ? 'Bhagya Ratna (9th Lord)' : ruledHouses.includes(5) ? 'Punya Ratna (5th Lord)' : ruledHouses.includes(10) ? 'Karma / Promotion Ratna' : ruledHouses.includes(4) ? 'Sukha / Griha Ratna' : `${planet} Ratna`),
    categoryBadge: roleContext?.customCategoryBadge || (isLagnaLord ? 'Lifelong Trinity' : isYogakaraka ? 'Supreme Yogakaraka' : isTrikona ? 'Trikona Trinity' : isContraindicated ? 'Prohibited' : 'Kendra Advisor'),
    suitabilityScore: score,
    isRecommended,
    isContraindicated,
    reasons,
    cautions,
    audits: {
      ruledHouses,
      isLagnaLord,
      isYogakaraka,
      isDusthanaLord,
      isBadhakaLord,
      isMarakaLord,
      tara,
      savBindus,
      bavBindus,
      savPassed,
      bavPassed,
      charaKaraka,
      dignity,
      housePlaced,
      d9Dignity,
      shadbalaRatio
    }
  };
}

/**
 * Evaluates the entire chart across all 5 Classical Perspectives
 */
export function analyzeVedicGemology(kundali: CompleteKundali): VedicGemologyAnalysis {
  const ascSign = kundali.houses[0]?.sign || 'Aries';
  const ascSignIdx = SIGN_NAMES.indexOf(ascSign) >= 0 ? SIGN_NAMES.indexOf(ascSign) : 0;
  const ascLord = ZODIAC_LORDS_ARRAY[ascSignIdx];

  const moonPlanet = kundali.planets?.find(p => p.name === 'Moon');
  const moonSign = moonPlanet?.sign || 'Aries';
  const moonSignIdx = SIGN_NAMES.indexOf(moonSign) >= 0 ? SIGN_NAMES.indexOf(moonSign) : 0;
  const moonLord = ZODIAC_LORDS_ARRAY[moonSignIdx];
  const janmaNakshatra = moonPlanet?.nakshatra || kundali.panchang?.nakshatra || 'Ashwini';

  // 1. House Lords
  const lordOfHouse = (h: number): PlanetName => {
    const houseSignIdx = (ascSignIdx + h - 1) % 12;
    return ZODIAC_LORDS_ARRAY[houseSignIdx];
  };

  const lord1 = lordOfHouse(1);
  const lord4 = lordOfHouse(4);
  const lord5 = lordOfHouse(5);
  const lord9 = lordOfHouse(9);
  const lord10 = lordOfHouse(10);

  // Check for Yogakaraka (single planet owning a Kendra and a Trikona)
  let yogakarakaPlanet: PlanetName | undefined = undefined;
  if (['Taurus', 'Libra'].includes(ascSign)) {
    yogakarakaPlanet = 'Saturn'; // Taurus: 9 & 10; Libra: 4 & 5
  } else if (['Cancer', 'Leo'].includes(ascSign)) {
    yogakarakaPlanet = 'Mars'; // Cancer: 5 & 10; Leo: 4 & 9
  } else if (['Capricorn', 'Aquarius'].includes(ascSign)) {
    yogakarakaPlanet = 'Venus'; // Capricorn: 5 & 10; Aquarius: 4 & 9
  }

  // Evaluate All 9 Planets
  const allPlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
  const allEvaluations: Record<PlanetName, EvaluatedGemstone> = {} as any;

  allPlanets.forEach((p) => {
    allEvaluations[p] = evaluateSinglePlanetGemstone(p, kundali);
  });

  // Perspective 1: The Core Trikona Lifelong Trinity (1, 5, 9)
  const lifeStone = evaluateSinglePlanetGemstone(lord1, kundali, {
    perspectiveType: 'TRIKONA_LIFELONG',
    customTitleEn: 'Life Stone (Jeevan Ratna)',
    customTitleHi: 'Jeevan Ratna (Lagna Lord)',
    customCategoryBadge: '1st House (Lagna) • Vitality & Resilience'
  });

  const punyaStone = evaluateSinglePlanetGemstone(lord5, kundali, {
    perspectiveType: 'TRIKONA_LIFELONG',
    customTitleEn: 'Intellect / Karma Stone (Punya Ratna)',
    customTitleHi: 'Punya Ratna (5th Lord)',
    customCategoryBadge: '5th House (Purva Punya) • Intellect & Memory'
  });

  const bhagyaStone = evaluateSinglePlanetGemstone(lord9, kundali, {
    perspectiveType: 'TRIKONA_LIFELONG',
    customTitleEn: 'Fortune Stone (Bhagya Ratna)',
    customTitleHi: 'Bhagya Ratna (9th Lord)',
    customCategoryBadge: '9th House (Bhagya) • Destiny & Divine Favor'
  });

  // Perspective 2: Kendradhipati & Yogakaraka School (4, 10 & Yogakaraka)
  const sukhaStone = evaluateSinglePlanetGemstone(lord4, kundali, {
    perspectiveType: 'KENDRA_YOGAKARAKA',
    customTitleEn: 'Inner Peace & Asset Stone (Sukha Ratna)',
    customTitleHi: 'Sukha Ratna (4th Lord)',
    customCategoryBadge: '4th House (Sukha) • Mind, Mother & Property'
  });

  const karmaStone = evaluateSinglePlanetGemstone(lord10, kundali, {
    perspectiveType: 'KENDRA_YOGAKARAKA',
    customTitleEn: 'Career & Authority Stone (Karma Ratna)',
    customTitleHi: 'Karma / Promotion Ratna (10th Lord)',
    customCategoryBadge: '10th House (Karma) • Executive Power & Status'
  });

  let yogakarakaStone: EvaluatedGemstone | undefined = undefined;
  if (yogakarakaPlanet) {
    yogakarakaStone = evaluateSinglePlanetGemstone(yogakarakaPlanet, kundali, {
      perspectiveType: 'KENDRA_YOGAKARAKA',
      customTitleEn: 'Supreme Yogakaraka Stone',
      customTitleHi: 'Parama Yogakaraka Ratna (Kendra + Trikona Lord)',
      customCategoryBadge: `Yogakaraka for ${ascSign} • Combined Kendra + Trikona`
    });
  }

  // Perspective 3: Temporal Dasha Advisor
  const now = new Date().getTime();
  let currentMD = kundali.dashas?.find(d => {
    const s = new Date(d.startDate).getTime();
    const e = new Date(d.endDate).getTime();
    return now >= s && now <= e;
  }) || kundali.dashas?.[0];

  let currentAD = currentMD?.antardashas?.find(ad => {
    const s = new Date(ad.startDate).getTime();
    const e = new Date(ad.endDate).getTime();
    return now >= s && now <= e;
  });

  const mdPlanet = currentMD?.planet || 'Jupiter';
  const mdEval = evaluateSinglePlanetGemstone(mdPlanet, kundali, {
    perspectiveType: 'TEMPORAL_DASHA',
    customTitleEn: `Active Mahadasha Stone (${mdPlanet})`,
    customTitleHi: `Active Mahadasha Ratna (${mdPlanet})`,
    customCategoryBadge: `Vimshottari Mahadasha • ${currentMD ? new Date(currentMD.startDate).getFullYear() : ''} - ${currentMD ? new Date(currentMD.endDate).getFullYear() : ''}`
  });

  let mdVerdict: 'HIGHLY_RECOMMENDED' | 'CONDITIONALLY_SAFE' | 'STRICTLY_AVOID' = 'CONDITIONALLY_SAFE';
  let mdVerdictExplanation = '';

  if (mdEval.isContraindicated || !mdEval.audits.tara.isSafe || mdEval.audits.isDusthanaLord) {
    mdVerdict = 'STRICTLY_AVOID';
    mdVerdictExplanation = `Although you are currently running ${mdPlanet} Mahadasha, this planet is functionally discordant for your ${ascSign} Lagna (rules house ${mdEval.audits.ruledHouses.join(', ')} or occupies ${mdEval.audits.tara.nameEn} Tara). Do NOT wear ${mdEval.gemstone.primaryStone}; doing so will magnify trials. Perform Japa, charity, or wear its prescribed Rudraksha instead.`;
  } else if (mdEval.suitabilityScore >= 70) {
    mdVerdict = 'HIGHLY_RECOMMENDED';
    mdVerdictExplanation = `Wearing ${mdEval.gemstone.primaryStone} will strongly harmonize and amplify planetary energy during this active Mahadasha window (valid until ${currentMD?.endDate ? new Date(currentMD.endDate).toLocaleDateString() : 'Dasha End'}).`;
  } else {
    mdVerdict = 'CONDITIONALLY_SAFE';
    mdVerdictExplanation = `${mdPlanet} is moderately placed. You may wear its milder substitute (${mdEval.gemstone.substitutes[0] || 'Upratna'}) with a 3-day trial period.`;
  }

  let adEval: EvaluatedGemstone | undefined = undefined;
  let adVerdict: 'HIGHLY_RECOMMENDED' | 'CONDITIONALLY_SAFE' | 'STRICTLY_AVOID' = 'CONDITIONALLY_SAFE';
  let adVerdictExplanation = '';

  if (currentAD) {
    const adPlanet = currentAD.planet;
    adEval = evaluateSinglePlanetGemstone(adPlanet, kundali, {
      perspectiveType: 'TEMPORAL_DASHA',
      customTitleEn: `Active Antardasha Stone (${adPlanet})`,
      customTitleHi: `Active Antardasha Ratna (${adPlanet})`,
      customCategoryBadge: `Vimshottari Antardasha • Valid till ${new Date(currentAD.endDate).toLocaleDateString()}`
    });

    if (adEval.isContraindicated || !adEval.audits.tara.isSafe || adEval.audits.isDusthanaLord) {
      adVerdict = 'STRICTLY_AVOID';
      adVerdictExplanation = `Antardasha lord ${adPlanet} carries hostile lordship (Dusthana) or malefic Tara. Wearing its gem is not advised.`;
    } else if (adEval.suitabilityScore >= 70) {
      adVerdict = 'HIGHLY_RECOMMENDED';
      adVerdictExplanation = `Excellent short-term booster! Wearing ${adEval.gemstone.primaryStone} or its substitute during this Antardasha will catalyze focus and achievements.`;
    } else {
      adVerdict = 'CONDITIONALLY_SAFE';
      adVerdictExplanation = `Wear only after testing for 3 days or opt for chanting its Beeja Mantra.`;
    }
  }

  // Perspective 4: Jaimini Chara Karakas
  const akItem = kundali.charaKarakas?.find(c => c.code === 'AK') || { planet: lord1, code: 'AK' };
  const amkItem = kundali.charaKarakas?.find(c => c.code === 'AmK') || { planet: lord10, code: 'AmK' };

  const akPlanetPos = kundali.planets?.find(p => p.name === akItem.planet);
  const amkPlanetPos = kundali.planets?.find(p => p.name === amkItem.planet);

  const akEval = evaluateSinglePlanetGemstone(akItem.planet, kundali, {
    perspectiveType: 'JAIMINI_KARAKA',
    customTitleEn: `Atmakaraka Soul Gem (${akItem.planet})`,
    customTitleHi: `Atmakaraka Ratna (${akItem.planet})`,
    customCategoryBadge: 'Jaimini Atmakaraka (AK) • Soul Purpose & Liberation'
  });

  const amkEval = evaluateSinglePlanetGemstone(amkItem.planet, kundali, {
    perspectiveType: 'JAIMINI_KARAKA',
    customTitleEn: `Amatyakaraka Career Gem (${amkItem.planet})`,
    customTitleHi: `Amatyakaraka Ratna (${amkItem.planet})`,
    customCategoryBadge: 'Jaimini Amatyakaraka (AmK) • Career Mastery & Status'
  });

  // Perspective 5: Strictly Contraindicated Gems (Nishedha Ratna)
  const contraindicatedGems: EvaluatedGemstone[] = allPlanets
    .map(p => allEvaluations[p])
    .filter(g => g.isContraindicated || g.suitabilityScore < 40)
    .sort((a, b) => a.suitabilityScore - b.suitabilityScore);

  return {
    ascendantSign: ascSign,
    ascendantLord: ascLord,
    moonSign,
    moonLord,
    janmaNakshatra,
    trikonaTrinity: {
      lifeStone,
      punyaStone,
      bhagyaStone
    },
    kendraYogakaraka: {
      sukhaStone,
      karmaStone,
      yogakarakaStone
    },
    dashaAdvisor: {
      activeMahadasha: {
        planet: mdPlanet,
        startDate: currentMD?.startDate || '',
        endDate: currentMD?.endDate || '',
        evaluatedGem: mdEval,
        verdict: mdVerdict,
        verdictExplanation: mdVerdictExplanation
      },
      activeAntardasha: currentAD && adEval ? {
        planet: currentAD.planet,
        startDate: currentAD.startDate,
        endDate: currentAD.endDate,
        evaluatedGem: adEval,
        verdict: adVerdict,
        verdictExplanation: adVerdictExplanation
      } : undefined
    },
    jaiminiKarakas: {
      atmakaraka: {
        planet: akItem.planet,
        degree: akPlanetPos?.degreeFormatted || '',
        evaluatedGem: akEval,
        soulPurpose: `As your Atmakaraka (highest planetary degree at ${akPlanetPos?.degreeFormatted}), ${akItem.planet} represents your soul's chief curriculum in this incarnation. Wearing its stone or practicing its meditation deepens spiritual clarity and removes existential restlessness.`
      },
      amatyakaraka: {
        planet: amkItem.planet,
        degree: amkPlanetPos?.degreeFormatted || '',
        evaluatedGem: amkEval,
        careerInfluence: `As your Amatyakaraka (2nd highest degree at ${amkPlanetPos?.degreeFormatted}), ${amkItem.planet} is the "Prime Minister" of your chart governing career growth, executive influence, and worldly accomplishments.`
      }
    },
    contraindicatedGems,
    allEvaluations
  };
}

/**
 * Interactive Jewelry Conflict Auditor ("What are you currently wearing?")
 * Audits 1, 2, or 3 selected gemstones for mutual incompatibility and birth chart resonance.
 */
export interface JewelryAuditResult {
  selectedPlanets: PlanetName[];
  overallCompatibility: 'HARMONIOUS' | 'CAUTION_FRICTION' | 'SEVERE_CLASH';
  compatibilityScore: number; // 0 - 100
  verdictTitle: string;
  verdictSummary: string;
  pairwiseAudits: {
    planetA: PlanetName;
    planetB: PlanetName;
    gemA: string;
    gemB: string;
    status: 'MUTUAL_FRIENDS' | 'NEUTRAL' | 'MUTUAL_ENEMIES' | 'SEVERE_HOSTILE';
    explanation: string;
  }[];
  chartSafetyNotes: {
    planet: PlanetName;
    gem: string;
    isSafeForChart: boolean;
    reason: string;
  }[];
}

export function auditJewelryPairings(
  planets: PlanetName[],
  analysis: VedicGemologyAnalysis
): JewelryAuditResult {
  if (!planets || planets.length === 0) {
    return {
      selectedPlanets: [],
      overallCompatibility: 'HARMONIOUS',
      compatibilityScore: 100,
      verdictTitle: 'No Gemstones Selected',
      verdictSummary: 'Select 1 to 3 gemstones that you currently wear to analyze mutual planetary harmony and alignment with your birth chart.',
      pairwiseAudits: [],
      chartSafetyNotes: []
    };
  }

  const pairwiseAudits: JewelryAuditResult['pairwiseAudits'] = [];
  let severeClashCount = 0;
  let frictionCount = 0;

  for (let i = 0; i < planets.length; i++) {
    for (let j = i + 1; j < planets.length; j++) {
      const pA = planets[i];
      const pB = planets[j];
      const gemA = MASTER_GEMSTONES[pA]?.primaryStone || pA;
      const gemB = MASTER_GEMSTONES[pB]?.primaryStone || pB;

      const relA = PLANET_RELATIONSHIPS[pA];
      const isAEnemyOfB = relA.enemies.includes(pB);
      const isAFriendOfB = relA.friends.includes(pB);

      // Check famous classic hostile clashes
      const pairSet = new Set([pA, pB]);
      let status: JewelryAuditResult['pairwiseAudits'][0]['status'] = 'NEUTRAL';
      let explanation = '';

      if (pairSet.has('Sun') && pairSet.has('Saturn')) {
        status = 'SEVERE_HOSTILE';
        explanation = `Classic Father-Son mythological warfare (*Surya-Shani Virodha*). Sun represents pure fire, vitality, and authority, while Saturn represents ice, slow karmic delay, and renunciation. Wearing Ruby and Blue Sapphire together can trigger severe chronic health issues, intense ego friction, and unexpected reversals.`;
        severeClashCount++;
      } else if (pairSet.has('Sun') && pairSet.has('Venus')) {
        status = 'MUTUAL_ENEMIES';
        explanation = `Sun and Venus are natural enemies (*Surya-Shukra Virodha*). Royal pride clashes with sensory comfort and partnerships, creating marital tension or hormonal imbalances.`;
        frictionCount++;
      } else if (pairSet.has('Sun') && pairSet.has('Rahu')) {
        status = 'SEVERE_HOSTILE';
        explanation = `Solar Eclipse Combination (*Grahan Dosha*). Wearing Ruby and Hessonite (Gomed) together amplifies karmic illusions, heart/eye ailments, and sudden legal or reputational turmoil.`;
        severeClashCount++;
      } else if (pairSet.has('Moon') && pairSet.has('Rahu')) {
        status = 'SEVERE_HOSTILE';
        explanation = `Lunar Eclipse Combination (*Chandra-Rahu Grahan*). Wearing Pearl and Hessonite together creates psychic turbulence, phobias, deep mood swings, and insomnia.`;
        severeClashCount++;
      } else if (pairSet.has('Moon') && pairSet.has('Ketu')) {
        status = 'MUTUAL_ENEMIES';
        explanation = `Moon and Ketu combination (*Chandra-Ketu Yuti*). Can induce extreme emotional detachment, melancholy, or anxiety.`;
        frictionCount++;
      } else if (pairSet.has('Mars') && pairSet.has('Mercury')) {
        status = 'MUTUAL_ENEMIES';
        explanation = `Mars (impulsive passion/blood) and Mercury (analytical intellect/nerves) are bitter enemies (*Bhauma-Budha Virodha*). Wearing Red Coral and Emerald together causes nervous agitation, restless speech, and rash financial decisions.`;
        frictionCount++;
      } else if (pairSet.has('Mars') && pairSet.has('Saturn')) {
        status = 'SEVERE_HOSTILE';
        explanation = `Fire vs. Ice collision (*Kuja-Shani Sangharsha*). Acceleration versus braking: wearing Red Coral and Blue Sapphire simultaneously makes the aura accident-prone, irritable, and prone to explosive disputes.`;
        severeClashCount++;
      } else if (pairSet.has('Jupiter') && pairSet.has('Venus')) {
        status = 'MUTUAL_ENEMIES';
        explanation = `Deva Guru vs. Asura Guru (*Brihaspati-Shukra Dvandva*). While both are noble benefics, wearing Yellow Sapphire and Diamond together causes conflict between spiritual restraint and luxury/hedonism.`;
        frictionCount++;
      } else if (isAEnemyOfB) {
        status = 'MUTUAL_ENEMIES';
        explanation = `${pA} considers ${pB} a natural adversary. Combining ${gemA} and ${gemB} creates energetic disharmony.`;
        frictionCount++;
      } else if (isAFriendOfB) {
        status = 'MUTUAL_FRIENDS';
        explanation = `${pA} and ${pB} are natural harmonious allies. ${gemA} and ${gemB} synergize effectively.`;
      } else {
        status = 'NEUTRAL';
        explanation = `${pA} and ${pB} share a neutral vibrational frequency without direct conflict.`;
      }

      pairwiseAudits.push({
        planetA: pA,
        planetB: pB,
        gemA,
        gemB,
        status,
        explanation
      });
    }
  }

  // Chart Safety Notes
  const chartSafetyNotes = planets.map(p => {
    const evalData = analysis.allEvaluations[p];
    const gem = MASTER_GEMSTONES[p]?.primaryStone || p;
    return {
      planet: p,
      gem,
      isSafeForChart: !evalData.isContraindicated && evalData.suitabilityScore >= 50,
      reason: evalData.isContraindicated 
        ? `Rules house ${evalData.audits.ruledHouses.join(', ')} (Dusthana) or placed in dangerous ${evalData.audits.tara.nameEn} Tara. Highly hazardous for your chart.`
        : evalData.isRecommended 
        ? `Auspicious functional role (Score: ${evalData.suitabilityScore}/100) — safe for ${analysis.ascendantSign} Lagna.`
        : `Moderate alignment (Score: ${evalData.suitabilityScore}/100) — requires trial test.`
    };
  });

  const chartFailsCount = chartSafetyNotes.filter(n => !n.isSafeForChart).length;

  let overallCompatibility: JewelryAuditResult['overallCompatibility'] = 'HARMONIOUS';
  let compatibilityScore = 95 - (severeClashCount * 45) - (frictionCount * 20) - (chartFailsCount * 25);
  compatibilityScore = Math.max(5, Math.min(100, compatibilityScore));

  let verdictTitle = '';
  let verdictSummary = '';

  if (severeClashCount > 0 || chartFailsCount >= 2) {
    overallCompatibility = 'SEVERE_CLASH';
    verdictTitle = '⚠️ Severe Gemstone Conflict & Astrological Hazard';
    verdictSummary = `Your current combination contains hostile planetary frequencies (e.g. ${pairwiseAudits.find(a => a.status === 'SEVERE_HOSTILE')?.gemA} + ${pairwiseAudits.find(a => a.status === 'SEVERE_HOSTILE')?.gemB}) or includes stones strictly contraindicated for your ${analysis.ascendantSign} Ascendant. It is strongly advised to remove the clashing stone immediately.`;
  } else if (frictionCount > 0 || chartFailsCount === 1) {
    overallCompatibility = 'CAUTION_FRICTION';
    verdictTitle = '⚡ Moderate Vibrational Friction Detected';
    verdictSummary = `The stones you wear have mild ideological opposition or one of them does not fully support your birth chart. While not immediately lethal, it dampens your overall progress and creates subtle mental or physical resistance.`;
  } else {
    overallCompatibility = 'HARMONIOUS';
    verdictTitle = '✅ Auspicious & Synergistic Combination';
    verdictSummary = `All selected gemstones share mutual planetary friendship and align harmoniously with your ${analysis.ascendantSign} Lagna, Moon sign, and Navatara matrix.`;
  }

  return {
    selectedPlanets: planets,
    overallCompatibility,
    compatibilityScore,
    verdictTitle,
    verdictSummary,
    pairwiseAudits,
    chartSafetyNotes
  };
}
